/**
 * Signal-style Safety Number Generation
 * Produces a deterministic 60-digit fingerprint from two public keys.
 */

// Convert ArrayBuffer to hex string
function bufToHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Generate a 60-digit safety number from two public keys.
 * Both users will get the same number because keys are sorted first.
 *
 * Algorithm:
 * 1. Sort keys alphabetically
 * 2. Concatenate
 * 3. Iterate SHA-256 5200 times (matches Signal's approach)
 * 4. Take first 30 bytes → 60 decimal digits
 */
export async function generateSafetyNumber(
  myPublicKey: string,
  theirPublicKey: string
): Promise<string> {
  // Sort so both sides produce the same result
  const sorted = [myPublicKey, theirPublicKey].sort();
  const combined = sorted[0] + sorted[1];

  // Iterative hashing (5200 rounds like Signal)
  let data = new TextEncoder().encode(combined);
  for (let i = 0; i < 5200; i++) {
    const hash = await crypto.subtle.digest('SHA-256', data);
    data = new Uint8Array(hash);
  }

  // Convert first 30 bytes to 60 decimal digits
  const bytes = new Uint8Array(data.buffer).slice(0, 30);
  let digits = '';
  for (const byte of bytes) {
    // Each byte → 2 decimal digits (mod 100, zero-padded)
    digits += (byte % 100).toString().padStart(2, '0');
  }

  return digits;
}

/**
 * Format a 60-digit safety number into 12 groups of 5 digits.
 */
export function formatSafetyNumber(raw: string): string[] {
  const clean = raw.replace(/\s/g, '');
  const groups: string[] = [];
  for (let i = 0; i < clean.length; i += 5) {
    groups.push(clean.slice(i, i + 5));
  }
  return groups;
}

/**
 * Build the QR code payload for cross-device verification.
 */
export function buildQRPayload(
  myUserId: string,
  theirUserId: string,
  myPublicKey: string,
  theirPublicKey: string
): string {
  return JSON.stringify({
    version: 1,
    users: [myUserId, theirUserId].sort(),
    keys: [myPublicKey, theirPublicKey].sort(),
  });
}

// ─── Verification status persistence (IndexedDB) ───

const VERIFY_DB = 'elefa_verification';
const VERIFY_STORE = 'verified_contacts';

function openVerifyDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(VERIFY_DB, 1);
    req.onerror = () => reject(new Error('Failed to open verification DB'));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(VERIFY_STORE)) {
        db.createObjectStore(VERIFY_STORE, { keyPath: 'usersId' });
      }
    };
  });
}

export interface VerifiedContact {
  usersId: string; // sorted pair key
  keyHash: string; // hash of combined keys at verification time
  verifiedAt: number;
}

function pairKey(myId: string, theirId: string): string {
  return [myId, theirId].sort().join(':');
}

export async function markContactVerified(
  myId: string,
  theirId: string,
  safetyNumber: string
): Promise<void> {
  const db = await openVerifyDB();
  const entry: VerifiedContact = {
    usersId: pairKey(myId, theirId),
    keyHash: safetyNumber,
    verifiedAt: Date.now(),
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction([VERIFY_STORE], 'readwrite');
    tx.objectStore(VERIFY_STORE).put(entry);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(new Error('Failed to save verification')); };
  });
}

export async function getVerifiedContact(
  myId: string,
  theirId: string
): Promise<VerifiedContact | null> {
  try {
    const db = await openVerifyDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([VERIFY_STORE], 'readonly');
      const req = tx.objectStore(VERIFY_STORE).get(pairKey(myId, theirId));
      req.onsuccess = () => { db.close(); resolve(req.result || null); };
      req.onerror = () => { db.close(); reject(new Error('Failed to read verification')); };
    });
  } catch {
    return null;
  }
}
